import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ChemicalSortField, SortDirection } from './ChemicalResults';

interface Chemical {
  id: string;
  chemical_name: string;
  cas_number: string | null;
  chemical_type: string;
  supplier: string | null;
  molecular_formula: string | null;
  molecular_weight: number | null;
  hazard_level?: 'low' | 'moderate' | 'high' | 'unknown';
}

interface ChemicalTableProps {
  chemicals: Chemical[];
  sortField: ChemicalSortField;
  sortDirection: SortDirection;
  onSort: (field: ChemicalSortField) => void;
}

const getHazardIcon = (level: string | undefined) => {
  switch (level) {
    case 'low':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'moderate':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'high':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
  }
};

const getHazardColor = (level: string | undefined) => {
  switch (level) {
    case 'low':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'moderate':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'high':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const SortIcon = ({ field, sortField, sortDirection }: { 
  field: ChemicalSortField; 
  sortField: ChemicalSortField; 
  sortDirection: SortDirection; 
}) => {
  if (field !== sortField) {
    return <ArrowUpDown className="h-4 w-4" />;
  }
  return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
};

export const ChemicalTable = ({ chemicals, sortField, sortDirection, onSort }: ChemicalTableProps) => {
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Button 
                variant="ghost" 
                onClick={() => onSort('chemical_name')}
                className="h-auto p-0 font-semibold hover:bg-transparent"
              >
                Chemical Name
                <SortIcon field="chemical_name" sortField={sortField} sortDirection={sortDirection} />
              </Button>
            </TableHead>
            <TableHead>
              <Button 
                variant="ghost" 
                onClick={() => onSort('cas_number')}
                className="h-auto p-0 font-semibold hover:bg-transparent"
              >
                CAS Number
                <SortIcon field="cas_number" sortField={sortField} sortDirection={sortDirection} />
              </Button>
            </TableHead>
            <TableHead>
              <Button 
                variant="ghost" 
                onClick={() => onSort('chemical_type')}
                className="h-auto p-0 font-semibold hover:bg-transparent"
              >
                Type
                <SortIcon field="chemical_type" sortField={sortField} sortDirection={sortDirection} />
              </Button>
            </TableHead>
            <TableHead>
              <Button 
                variant="ghost" 
                onClick={() => onSort('supplier')}
                className="h-auto p-0 font-semibold hover:bg-transparent"
              >
                Supplier
                <SortIcon field="supplier" sortField={sortField} sortDirection={sortDirection} />
              </Button>
            </TableHead>
            <TableHead>Hazard Level</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {chemicals.map((chemical) => (
            <TableRow key={chemical.id} className="cursor-pointer hover:bg-muted/50">
              <TableCell className="font-medium">{chemical.chemical_name}</TableCell>
              <TableCell className="font-mono text-sm">
                {chemical.cas_number || 'N/A'}
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{chemical.chemical_type}</Badge>
              </TableCell>
              <TableCell>{chemical.supplier || 'Unknown'}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getHazardIcon(chemical.hazard_level)}
                  <Badge className={getHazardColor(chemical.hazard_level)}>
                    {chemical.hazard_level || 'Unknown'}
                  </Badge>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};